import { MessageDescriptor } from 'react-intl';
import { Sports } from '@gin-betting/constants';
import core, { App, GQL, Viewport } from '@gin-betting/core';
import { Record as ImRecord } from 'immutable';
import { partition, uniq, values } from 'lodash';

import {
    BettingSettingsOptions,
    BettingWidgetsOptions,
    FullStateShape,
    IBetslipAttrManager,
    SkipInitialRequestByPage,
    State,
    TimeUnit,
} from 'types';
import getExtraMatchListRoutes from 'utils/getExtraMatchListRoutes';
import globalMessages from './messages';

const {
    utils: { getBettingOptions },
} = core;

export enum EventType {
    Redirect = 'redirect',
    OpenBetsHistory = 'open-bets-history',
    HandleNotEnoughBalance = 'handle-not-enough-balance',
    HandleNotEnoughDocs = 'handle-not-enough-docs',
    BetConfirm = 'bet-confirm',
    AddOdd = 'add-odd',
    OpenDeposit = 'open-deposit',
}

export enum Destination {
    Sport = 'sport',
    Esport = 'esport',
    MixSports = 'mix-sports',
    Login = 'login',
    Outright = 'outright',
    Live = 'live',
    Results = 'results',
    Statistics = 'statistics',
}

export enum SidebarView {
    betslip = 'betslip',
    bets = 'bets',
    promo = 'promo',
    live = 'live',
}

export enum MatchListType {
    UPCOMING = 'upcoming',
    RESULTS = 'results',
    LIVE = 'live',
    PREMATCH = 'prematch',
}

export enum BettingWidgets {
    BETSLIP_SIDEBAR = 'betslipSidebar',
    CATEGORIZER = 'categorizer',
    SETTINGS = 'settings',
    MOBILE_HEADER = 'mobileHeader',
    NAVIGATION = 'navigation',
    STREAM = 'stream',
}

export enum LocalRouts {
    FAVORITE_UPCOMING = '/favorite-upcoming',
    FAVORITE_RESULTS = '/favorite-results',
    FAVORITE_OUTRIGHTS = '/favorite-outrights',
}

export const CLASS_NAME_OF_DISABLED_PAGE = '__app--is-page-disabled' as const;
export const CLASS_NAME_OF_DISABLED_SCROLL = '__app--is-scroll-disabled' as const;
export const CLASS_NAME_OF_BETTING_COMPONENTS = '__app--is-betting' as const;
export const CSS_ID_OF_SETTINGS_MENU = '__app-oddFormat-select' as const;
export const CLASS_NAME_UPDATE_COLORS = '__app-Update-colors' as const;

export const DEFAULT_WIDGET_OPTIONS: BettingWidgetsOptions = {
    [BettingWidgets.BETSLIP_SIDEBAR]: {},
    [BettingWidgets.CATEGORIZER]: {},
    [BettingWidgets.MOBILE_HEADER]: {
        containerElementId: 'header-betting-block',
        isSettingsEnabled: true,
    },
    [BettingWidgets.NAVIGATION]: {
        isShowExtraNavigation: false,
    },
    [BettingWidgets.SETTINGS]: { containerElementId: CSS_ID_OF_SETTINGS_MENU },
    [BettingWidgets.STREAM]: {
        scrollToFirstStream: true,
        pinMinimizedStreamOutOfView: true,
        disableTheaterMode: false,
        disableMatchTracker: false,
        ignoreOffsetInTheaterMode: false,
    },
};

export const ROOT_STYLE_ID_ELEMENT = 'betting-style-root' as const;

export const DEFAULT_BREAKPOINTS: Viewport.BreakpointsSettings = {
    superExtraSmall: 360,
    extraSmall: 480,
    small: 768,
    medium: 1096,
    large: 1347,
    major: 1366,
    extraMajor: 1439,
    great: 1560,
    extraLarge: 1920,
};

export const marketGroupDivider = '~';

const stateShape: {
    [key in keyof FullStateShape]: undefined;
} = {
    entities: undefined,
    filters: undefined,
    betslip: undefined,
    viewport: undefined,
    ui: undefined,
    match: undefined,
    matchList: undefined,
    pagination: undefined,
    freebet: undefined,
    favorite: undefined,
    stream: undefined,
    banners: undefined,
    favoriteMarkets: undefined,
    promo: undefined,
};

export const AppStateRecord = ImRecord<
    FullStateShape | Partial<FullStateShape>
>(stateShape, 'AppState');

export const MATCHES_LIMIT_PER_PAGE = 20;
export const MOBILE_MATCHES_LIMIT_PER_PAGE = 12;
export const NEWS_MATCHES_LIMIT_PER_PAGE = 10;
export const OUTRIGHTS_LIMIT_PER_PAGE = 100;
export const POLLING_EDGE_PAGE = 2; // no polling below that pagination page

export const ExtendedRouts = {
    ...App.Routes,
    ...LocalRouts,
};

export const PublicRoutes = [
    ExtendedRouts.UPCOMING_LIST,
    ExtendedRouts.LIVE_LIST,
    ExtendedRouts.PREMATCH,
    ExtendedRouts.OUTRIGHTS_LIST,
    ExtendedRouts.RESULTS_LIST,
    ExtendedRouts.FAVORITE_UPCOMING,
    ExtendedRouts.FAVORITE_OUTRIGHTS,
    ExtendedRouts.FAVORITE_RESULTS,
    ExtendedRouts.CLASSIC,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.LIVE_LIST}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.PREMATCH}`,
    ExtendedRouts.ESPORT,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.LIVE_LIST}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.PREMATCH}`,
];

export const SlugPublicRoutes = [
    ExtendedRouts.MATCH,
    ExtendedRouts.TOURNAMENT,
    ExtendedRouts.OUTRIGHT,
    ExtendedRouts.RESULT_MATCH,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.MATCH}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.MATCH}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.TOURNAMENT}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.TOURNAMENT}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.OUTRIGHT}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.OUTRIGHT}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.RESULT_MATCH}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.RESULT_MATCH}`,
];

export const favoritePages = {
    upcoming: ([ExtendedRouts.FAVORITE_UPCOMING] as any) as App.Routes[],
    result: ([ExtendedRouts.FAVORITE_RESULTS] as any) as App.Routes[],
    outright: ([ExtendedRouts.FAVORITE_OUTRIGHTS] as any) as App.Routes[],
};

export const BASE_CURRENCY = 'EUR' as const;

export const BET_STEPS = {
    10: 1,
    50: 5,
    100: 10,
    500: 50,
    1000: 100,
    5000: 500,
    10000: 1000,
    50000: 5000,
    100000: 10000,
    Infinity: 50000,
} as const;

export const DEFAULT_MAX_STAKE = 99999 as const;

// TODO: Move to core constant, use in "getBettingOptions" util
export const BETTING_OPTIONS_KEY = 'bettingOptions' as const;
export const DEBUG_INFO_LOCALSTORAGE_KEY = 'debug-info' as const;
export const ANALYTIC_LOCALSTORAGE_KEY = '__app-analytic' as const;
export const ENABLE_PLACE_BET_LOCALSTORAGE_KEY = '__app-enable-place-bet' as const;
export const DISABLE_PARTICIPANT_FEATURES_LOCALSTORAGE_KEY = '__app-disable-participant-features' as const;

export const VIEWABLE_SPORT_LIST = [
    Sports.FOOTBALL,
    Sports.BASKETBALL,
    Sports.ICE_HOCKEY,
    Sports.TENNIS,
    Sports.AMERICAN_FOOTBALL,
    Sports.HANDBALL,
    Sports.VOLLEYBALL,
    Sports.BEACH_VOLLEYBALL,
    Sports.TABLE_TENNIS,
    Sports.BADMINTON,
    Sports.RUGBY,
    Sports.RUGBY_LEAGUE,
    Sports.BASEBALL,
    Sports.CRICKET,
    Sports.DARTS,
    Sports.FUTSAL,
    Sports.SNOOKER,
] as const;

export enum SportEventMetaKey {
    BetradarId = 'betradar_id',
    BetradarLMTCoverageLevel = 'betradar_lmt_coverage_level',
    Time = 'time',
    TimerRunning = 'timer_running',
    Server = 'server', // NOTE: exist on App.MetaTypes
    Var = 'var',
    Bo = 'bo',
    GameState = 'game_state', // NOTE: exist on App.MetaTypes
    Map = 'map', // NOTE: exist on App.MetaTypes
    SideAway = 'side_away',
    SideHome = 'side_home',
    StateNumber = 'state_number', // NOTE: exist on App.MetaTypes
    Round = 'round', // NOTE: exist on App.MetaTypes
    OriginalId = 'original_id',
    SetState = 'set_state', // NOTE: exist on App.MetaTypes
}

export enum MetaSide {
    Terrorist = 't',
    ContrTerrorist = 'ct',
    Radiant = 'radiant',
    Dire = 'dire',
}

export enum SetState {
    TieBreak = 'tie_break',
}

export enum CategorizerTournamentMetaKey {
    CountryCode = 'country_code',
    Logo = 'logo',
    Slug = 'slug',
    Organization = 'organization',
    OrganizationLocalized = 'organization_localized',
    OrganizationLogo = 'organization_logo',
    IsTop = 'is_top',
    LiveEventsCount = 'live_events_count',
    PrematchEventsCount = 'prematch_events_count',
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SELECTOR_STUB = (_state: State, _props?: object): null => null;

export const DEFAULT_STREAM_URL = 'about:blank' as const;

export const SKIP_MATCH_LIST_INITIAL_REQUEST: SkipInitialRequestByPage = {
    [App.Routes.PREMATCH]: {
        pages: [App.Routes.PREMATCH],
        filterKey: 'dateTo',
    },
    [App.Routes.LIVE_LIST]: {
        pages: [App.Routes.LIVE_LIST],
        filterKey: 'dateTo',
    },
};

export const matchListRoutes = getExtraMatchListRoutes(ExtendedRouts);

if (__DEV__) {
    window.bettingOptions.sportIds = values(Sports);
}

// TODO: [GINFE-1360] remove hardcode
export const [esportIds, classicIds] = partition(
    uniq(getBettingOptions().sportIds),
    sportId => sportId.includes('esports_')
);

export const AVAILABLE_ROUTES_WITH_PARENT_ROUTE = [
    App.Routes.UPCOMING_LIST,
    App.Routes.LIVE_LIST,
    App.Routes.PREMATCH,
];

export const enum TOP_MARKETS_TYPE {
    SINGLE = 'SINGLE',
    MULTIPLE = 'MULTIPLE',
}

export const TOP_MARKETS_TYPE_MAPPING = {
    // Esports
    [Sports.ESPORTS_DOTA_2]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_FIFA]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_BASKETBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_ICE_HOCKEY]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_NBA_2K18]: TOP_MARKETS_TYPE.SINGLE,
    [Sports.ESPORTS_LEAGUE_OF_LEGENDS]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_HEROES_OF_THE_STORM]: TOP_MARKETS_TYPE.SINGLE,
    [Sports.ESPORTS_STREET_FIGHTER_5]: TOP_MARKETS_TYPE.SINGLE,
    [Sports.ESPORTS_ARTIFACT]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_HEARTHSTONE]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_COUNTER_STRIKE]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ESPORTS_TENNIS]: TOP_MARKETS_TYPE.MULTIPLE,
    // Classic
    [Sports.FOOTBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.BASKETBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.TENNIS]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.ICE_HOCKEY]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.VOLLEYBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.BASEBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.BOXING]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.FUTSAL]: TOP_MARKETS_TYPE.SINGLE,
    [Sports.HANDBALL]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.MMA]: TOP_MARKETS_TYPE.MULTIPLE,
    [Sports.SNOOKER]: TOP_MARKETS_TYPE.SINGLE,
    [Sports.AMERICAN_FOOTBALL]: TOP_MARKETS_TYPE.MULTIPLE,
};

export enum CMS_LINKS {
    STATISTIC = '/sport-statistics',
}

export const ACTIVE_MARKET_STATUSES = [
    GQL.MarketStatus.ACTIVE,
    GQL.MarketStatus.SUSPENDED,
];

export const LIVE_SPORTEVENT_STATUSES = [
    GQL.SportEventStatus.LIVE,
    GQL.SportEventStatus.SUSPENDED,
];

export const RESULTED_ODD_STATUSES = [
    GQL.OddStatus.WIN,
    GQL.OddStatus.HALF_WIN,
    GQL.OddStatus.LOSS,
    GQL.OddStatus.HALF_LOSS,
    GQL.OddStatus.REFUNDED,
] as const;

export const betTypesMessage: { [key in GQL.BetType]: MessageDescriptor } = {
    [GQL.BetType.SINGLE]: globalMessages.SINGLE,
    [GQL.BetType.EXPRESS]: globalMessages.EXPRESS,
    [GQL.BetType.SYSTEM]: globalMessages.SYSTEM,
};

export const TIME_UNIT_IN_MILLISECONDS: Record<TimeUnit, number> = {
    minute: 1000 * 60,
    hour: 1000 * 60 * 60,
    day: 1000 * 60 * 60 * 24,
};

export enum Product {
    ru = 'GGBet.ru',
    ua = 'GGBet.ua',
    com = 'GGBet.com',
    bet = 'GG.BET',
    unknown = 'unknown',
}

export const TITLES_BY_SPECIFIER = {
    top: globalMessages.top,
    handicap: globalMessages.marketHandicap,
    total: globalMessages.marketTotal,
} as const;

export const PLATFORM_BODY_ATTR = 'data-betting-platform' as const;
export const ODD_FORMAT_BODY_ATTR = 'data-betting-odd-format' as const;
export const ROOT_CSS_SCORE = ':root' as const;

export const DARK_THEME = 'dark' as const;

export enum Platform {
    MOBILE = 'mobile',
    DESKTOP = 'desktop',
}

export enum BettingSettingsKeys {
    THEMES = 'theme',
    ODD_FORMAT = 'oddFormat',
    CALLBACKS = 'callbacks',
    IS_ENABLE_ANALYTICS = 'isEnableAnalytics',
}

export const DEFAULT_BETTING_SETTINGS: BettingSettingsOptions = {
    [BettingSettingsKeys.ODD_FORMAT]: App.OddFormat.Decimal,
    [BettingSettingsKeys.CALLBACKS]: {},
    [BettingSettingsKeys.IS_ENABLE_ANALYTICS]: false,
    [BettingSettingsKeys.THEMES]: DARK_THEME,
};

export const MARKETS_ACTIVE_STATUSES: GQL.MarketStatus[] = [
    GQL.MarketStatus.ACTIVE,
    GQL.MarketStatus.SUSPENDED,
];

export const ODD_WIN_STATUSES: GQL.OddStatus[] = [
    GQL.OddStatus.WIN,
    GQL.OddStatus.HALF_WIN,
];

export const ODD_LOSS_STATUSES: GQL.OddStatus[] = [
    GQL.OddStatus.LOSS,
    GQL.OddStatus.HALF_LOSS,
];

export const countryReg = new RegExp(/\(([a-zA-Z]{2}-)?[a-zA-Z]{2,8}\)/, 'g');

export const BetslipAttrManager: IBetslipAttrManager = {};

export const FILTER_LOCATION_SEARCH: Array<keyof App.Filters> = [
    'dateTo',
    'dateFrom',
    'tournamentIds',
    'tournamentId',
    'freeBetId',
    'betslipOdds',
    'limit',
    'offset',
];

export enum CompareStatus {
    Greater = 'greater',
    Smaller = 'smaller',
    Equal = 'equal',
}

export enum MarketTag {
    EnumerableOddCompetitor = 'enumerable-odd-competitor',
}

export const RACE_SPORTS = [Sports.HORSE_RACING] as const;

export const BET_UNSETTLED_STATUSES = [
    GQL.BetStatus.PLACED,
    GQL.BetStatus.ARBITRAGE_ACCEPTED,
    GQL.BetStatus.ACCEPTED,
    GQL.BetStatus.UNSETTLED,
] as const;

export const BET_SETTLED_STATUSES = [
    GQL.BetStatus.SETTLED,
    GQL.BetStatus.ROLLED_BACK,
];
export const OUTRIGHTS_ONLY_SPORTS = [
    Sports.ESPORTS_BATTLEGROUNDS,
    Sports.HORSE_RACING,
] as const;
